import { useEffect, type RefObject } from "react";
import {
  isDashboardLayoutDebugEnabled,
  logDashboardViewportMetrics,
  measureDashboardViewport,
} from "@shared/utils/dashboardViewportFit";

/**
 * Scales the dashboard to fit the available main-layout height without page
 * or in-card scrollbars.
 */
export function useDashboardViewportScale(
  hostRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const shouldUseNaturalMobileScroll = () =>
      window.matchMedia(
        "(max-width: 768px), (max-height: 520px)",
      ).matches;

    let frame = 0;
    let settleTimer: number | undefined;

    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (shouldUseNaturalMobileScroll()) {
          const target = host.querySelector(
            ".dashboard-fit-target",
          ) as HTMLElement | null;
          if (target) {
            target.style.setProperty("--dashboard-fit-scale", "1");
            target.style.removeProperty("transform");
            if ("zoom" in target.style) {
              (target.style as CSSStyleDeclaration & { zoom: string }).zoom =
                "1";
            }
          }
          host.style.removeProperty("height");
          return;
        }

        const metrics = measureDashboardViewport(host);
        if (metrics) {
          logDashboardViewportMetrics(metrics);
        }
      });
    };

    const scheduleSettlePass = () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(measure, 250);
    };

    measure();
    scheduleSettlePass();

    const target = host.querySelector(".dashboard-fit-target") as HTMLElement | null;
    const scrollHost = host.closest(".main-layout-scroll") as HTMLElement | null;
    const observer = new ResizeObserver(() => {
      measure();
      scheduleSettlePass();
    });

    if (target) observer.observe(target);
    if (scrollHost) observer.observe(scrollHost);

    const mutationObserver = new MutationObserver(scheduleSettlePass);
    if (target) {
      mutationObserver.observe(target, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure).catch(() => undefined);

    if (isDashboardLayoutDebugEnabled()) {
      console.info(
        "[dashboard-fit] debug enabled — localStorage.dashboardLayoutDebug=1",
      );
      (window as Window & { __dashboardLayoutDebug?: () => unknown }).__dashboardLayoutDebug =
        () => measureDashboardViewport(host);
    }

    return () => {
      cancelAnimationFrame(frame);
      if (settleTimer) window.clearTimeout(settleTimer);
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [hostRef]);
}
